/*
 * The assistant's short markdown → safe HTML, for the /tutorial "Ask" box.
 *
 * Everything is escaped first; only bold, lists, "marker N" discs and
 * `[title](#step)` links are turned back into markup. The trailing "Sources:"
 * block is dropped — its steps are shown as the screenshot and the chips beside
 * the answer instead.
 *
 * `partial` is for text still streaming in: everything from a "Sources" label
 * on, and a half-written link line, are held back so they never flash up.
 */
const escapeHtml = (text) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const SOURCES_LABEL = /^\s*(sources?|bronnen|bron|quellen?|fuentes?|sorgenti|fonti|źródła)\s*:?\s*$/iu;
const LINK_ONLY = /^(-\s*)?(\p{L}+:\s*)?(\[[^\]]+\]\(#[a-z0-9-]+\)[\s,]*)+$/u;

// The disc matching a numbered marker on the screenshot. `notranslate` keeps
// Google Translate from rewriting the number.
const disc = (n) => `<span class="tut-marker notranslate" translate="no" aria-label="marker ${n}">${n}</span>`;

export const markerDisc = disc;

function inline(text) {
  return (
    text
      .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
      // "(marker 2)", "(marker 2 in the screenshot)" or a bare "marker 2".
      .replace(
        /\((?:see )?marker (\d+)(?: (?:in|on) the screenshot)?\)|\bmarker (\d+)(?: (?:in|on) the screenshot)?/gi,
        (_, a, b) => disc(a || b)
      )
      .replace(/\[([^\]]+)\]\(#([a-z0-9-]+)\)/g, '<a href="#" data-step="$2" class="link">$1</a>')
  );
}

export function renderAnswer(markdown, { partial = false } = {}) {
  let lines = escapeHtml(markdown || "").split(/\r?\n/);
  if (partial) {
    const cut = lines.findIndex((line) => SOURCES_LABEL.test(line) || /^\s*\p{L}+:\s*\[/u.test(line));
    if (cut !== -1) lines = lines.slice(0, cut);
    const last = lines[lines.length - 1] || "";
    if (/^\s*(-\s*)?\[/.test(last)) lines = lines.slice(0, -1);
  }

  const kept = [];
  for (const line of lines) {
    const t = line.trim();
    if (SOURCES_LABEL.test(t) && kept.length) continue;
    if (LINK_ONLY.test(t)) continue;
    kept.push(line);
  }

  let html = "";
  let list = null;
  for (const raw of kept) {
    const t = raw.trim();
    const bullet = t.match(/^[-•*]\s+(.*)$/);
    const numbered = t.match(/^\d+[.)]\s+(.*)$/);
    const kind = bullet ? "ul" : numbered ? "ol" : null;
    if (list && kind !== list) {
      html += `</${list}>`;
      list = null;
    }
    if (kind) {
      if (!list) {
        html += `<${kind}>`;
        list = kind;
      }
      html += `<li>${inline((bullet || numbered)[1])}</li>`;
    } else if (t) {
      html += `<p>${inline(t)}</p>`;
    }
  }
  if (list) html += `</${list}>`;
  return html;
}
